import { Button, Card, HSpacer, IconButton, Text, VSpacer } from "@/components/DesignSystem";
import { AddedProductsSideSheet } from "@/pages/Promotions/PromotionModal/AddedProductsSideSheet";
import { EditLocationsModal } from "@/pages/Promotions/PromotionModal/EditLocationsModal";
import { getSelectedLocationsText } from "@/pages/Promotions/PromotionModal/Helpers";
import { PromotionInputs } from "@/pages/Promotions/PromotionModal/PromotionModal";
import { getLoyaltyPointsText } from '@/utilities/PromotionUtils';
import { ApiRetailer, ApiRetailerLocation, ApiSubwallet } from "@api/interfaces";
import { ApiRewardsProgram } from "@api/interfaces/ApiRewardsProgram";
import Check from "@mui/icons-material/Check";
import AddIcon from "@mui/icons-material/Add";
import CloseIcon from "@mui/icons-material/Close";
import EditIcon from "@mui/icons-material/Edit";
import { Divider, TextField, useTheme } from "@mui/material";
import { Stack } from "@mui/system";
import { SponsorType } from "@shared/enums";
import { RewardsType } from "@shared/enums/RewardsType";
import { RewardsUom } from "@shared/enums/RewardsUom";
import { formatMediumDate, getCurrencyForLocale, getJsDateFromString } from '@shared/utilities';
import i18next from 'i18next';
import React, { useState } from "react";
import { useTranslation } from 'react-i18next';

const SectionDivider = (
  <>
    <VSpacer size="3" />
    <Divider />
    <VSpacer size="3" />
  </>
);

const EditSectionSubLine = ({ label, value } : {
  label: string,
  value: boolean,
}) => {
  return <Stack direction="row">
    {value
      ? <Check color="success" sx={{ width: "20px", height: "20px" }} />
      : <CloseIcon color="error" sx={{ width: "20px", height: "20px" }} />
    }
    <HSpacer size="3" />
    <Text category="body-medium">{label}</Text>
  </Stack>;
};

interface EditSectionProps {
  children?: React.ReactNode,
  disabled?: boolean,
  isImmutable?: boolean,
  label: string,
  onEditButtonClicked: () => void,
  onValueClicked?: () => void,
  value?: string,
}

const EditSection = ({
  children,
  disabled,
  isImmutable,
  label,
  onEditButtonClicked,
  onValueClicked,
  value,
} : EditSectionProps) => {
  const theme = useTheme();
  const immutableTextColor = isImmutable ? theme.palette.text.disabled : undefined;

  return <>
    <Stack alignItems="center" direction="row" justifyContent="space-between">
      <Text category="body-large" sx={{ color: immutableTextColor }} textAlign="left">
        {label}
      </Text>
      <Stack alignItems="center" direction="row">
        {(onValueClicked && !isImmutable)
          ? <Button
            color="primary"
            onClick={onValueClicked}
            testID='locations-edit-button'
            variant='text'
          >
            <Text category="body-large" textAlign="right">{value}</Text>
          </Button>
          : <Text category="body-large" sx={{ color: immutableTextColor }} textAlign="right">
            {value}
          </Text>
        }
        <HSpacer size="5" />
        {!isImmutable && (
          <IconButton
            disabled={disabled}
            onClick={onEditButtonClicked}
            testID='promotion-edit-icon'
          >
            <EditIcon color={disabled ? "disabled" : "primary"} />
          </IconButton>
        )}
        <HSpacer size="2" />
      </Stack>
    </Stack>
    <VSpacer size="2" />
    {children}
  </>;
};

const AdditionalInfoSection = ({
  isEditing,
  onCancel,
  onEditButtonClicked,
  onSave,
  promotion,
} : {
  isEditing: boolean,
  onCancel: () => void,
  onEditButtonClicked: () => void,
  onSave: (description: string) => void,
  promotion: PromotionInputs,
}) => {
  const theme = useTheme();
  const { t } = useTranslation();
  const [description, setDescription] = useState('');

  if (isEditing) {
    return <Stack>
      {SectionDivider}
      <VSpacer size="3" />
      <Text category="body-large">
        {t($ => $.promotions.create.step4.additionalInfo)}
      </Text>
      <VSpacer size="3" />
      <Text category="body-small" sx={{ color: theme.palette.text.secondary }}>
        {t($ => $.promotions.create.step4.additionalInfoHelp)}
      </Text>
      <VSpacer size="5" />
      <TextField
        defaultValue={promotion.description}
        multiline
        onChange={(e) => setDescription(e.target.value)}
        rows={12}
        variant="filled"
      />
      <VSpacer size="5" />
      <Stack direction="row" justifyContent="end">
        <Button
          onClick={onCancel}
          square
          testID="additional-info-cancel-button"
          variant="outlined"
        >
          <Text category="label-medium">
            {t($ => $.common.actions.cancel)}
          </Text>
        </Button>
        <HSpacer size="5" />
        <Button
          onClick={() => onSave(description)}
          square
          testID="additional-info-save-button"
        >
          <Text category="label-medium">
            {t($ => $.common.actions.save)}
          </Text>
        </Button>
        <HSpacer size="3" />
      </Stack>
    </Stack>;
  }

  if (promotion.description) {
    return <>
      {SectionDivider}
      <EditSection
        label={t($ => $.promotions.create.step4.additionalInfo)}
        onEditButtonClicked={onEditButtonClicked}
      >
        <Text category="body-small" sx={{ color: theme.palette.text.secondary }}>
          {t($ => $.promotions.create.step4.additionalInfoHelp)}
        </Text>
        <VSpacer size="5" />
        <Text category="body-large" sx={{ whiteSpace: 'pre-line' }}>
          {promotion.description}
        </Text>
      </EditSection>
      <VSpacer size="5" />
      {SectionDivider}
    </>;
  }

  return <Card testID="additional-information-card">
    <Stack direction="row" justifyContent="space-between">
      <Stack>
        <Text category="body-large">
          {t($ => $.promotions.create.step4.additionalInfo)}
        </Text>
        <VSpacer size="2" />
        <Text category="body-medium" sx={{ color: theme.palette.text.secondary }}>
          {t($ => $.promotions.create.step4.additionalInfoHelp)}
        </Text>
      </Stack>
      <Button
        onClick={onEditButtonClicked}
        square
        startIcon={<AddIcon sx={{ width: "14px" }}/>}
        sx={{ padding: "0px 12px", height: "32px" }}
        testID="add-additional-info-button"
      >
        <Text category="label-medium" sx={{ paddingLeft: "0px" }}>
          {t($ => $.common.actions.add)}
        </Text>
      </Button>
    </Stack>
  </Card>;
};

interface WorkflowStep4Props {
  activeRewardsProgram?: ApiRewardsProgram | null,
  canEditExpirationDate?: boolean,
  isEditMode?: boolean,
  navigateToStep: (step: number) => void,
  onPromotionChanged: (promotion: PromotionInputs) => void,
  promotion: PromotionInputs,
  retailerLocations?: ApiRetailerLocation[],
  selectedRetailer: ApiRetailer,
  subwallets?: ApiSubwallet[],
}

export const WorkflowStep4 = ({
  activeRewardsProgram,
  canEditExpirationDate,
  isEditMode,
  navigateToStep,
  onPromotionChanged,
  retailerLocations,
  promotion,
  selectedRetailer,
  subwallets,
}: WorkflowStep4Props) => {
  const { t } = useTranslation();
  const [isEditingAdditionalInfo, setIsEditingAdditionalInfo] = useState(false);
  const [showProductsForEarningSideSheet, setShowProductsForEarningSideSheet] = useState(false);
  const [showProductsForRedeemingSideSheet, setShowProductsForRedeemingSideSheet] = useState(false);
  const [showEditLocationsModal, setShowEditLocationsModal] = useState(false);

  const formatDate = (date?: string | null) => {
    if (date) {
      const jsDate = getJsDateFromString(date);
      return formatMediumDate(jsDate, i18next);
    }
  };

  const totalProductsForEarning = promotion.productIdsForEarningPoints?.length ?? 0;
  const totalProductsForRedeeming = promotion.productIdsForRedeemingPoints?.length ?? 0;

  const selectedSubwallet = subwallets?.find(s => s.id === promotion.subwalletId);
  return <>
    <Divider />
    <VSpacer size="3" />
    <EditSection
      disabled={isEditingAdditionalInfo}
      label={t($ => $.promotions.create.step4.promotionName)}
      onEditButtonClicked={() => navigateToStep(0)}
      value={promotion.name}
    />
    {SectionDivider}
    <EditSection
      disabled={isEditingAdditionalInfo}
      isImmutable={isEditMode}
      label={t($ => $.promotions.create.step4.retailer)}
      onEditButtonClicked={() => navigateToStep(0)}
      value={selectedRetailer.name}
    />
    {SectionDivider}
    <EditSection
      disabled={isEditingAdditionalInfo}
      isImmutable={isEditMode}
      label={t($ => $.promotions.create.step4.retailerLocations)}
      onEditButtonClicked={() => navigateToStep(0)}
      onValueClicked={promotion.allLocations ? undefined : () => setShowEditLocationsModal(true)}
      value={promotion.allLocations
        ? t($ => $.promotions.create.step4.allLocations)
        : getSelectedLocationsText(
          promotion.locationIds?.length ?? 0,
          retailerLocations?.length ?? 0,
        )
      }
    />
    {SectionDivider}
    <EditSection
      disabled={isEditingAdditionalInfo}
      isImmutable={isEditMode}
      label={t($ => $.promotions.create.step4.promotionSponsor)}
      onEditButtonClicked={() => navigateToStep(0)}
      value={promotion.sponsorType}
    />
    {SectionDivider}
    <EditSection
      disabled={isEditingAdditionalInfo}
      label={t($ => $.promotions.create.step4.runningDates)}
      onEditButtonClicked={() => navigateToStep(0)}
      value={`${formatDate(promotion.startDate)} – ${formatDate(promotion.endDate)}`}
    />
    {SectionDivider}
    <EditSection
      disabled={isEditingAdditionalInfo}
      isImmutable={isEditMode}
      label={t($ => $.promotions.create.step4.subwallet)}
      onEditButtonClicked={() => navigateToStep(0)}
      value={selectedSubwallet?.name}
    />
    {SectionDivider}
    <EditSection
      disabled={isEditingAdditionalInfo}
      isImmutable={!canEditExpirationDate}
      label={t($ => $.promotions.create.step4.pointsExpiration)}
      onEditButtonClicked={() => navigateToStep(0)}
      value={`${formatDate(promotion.pointsExpirationDate)}`}
    />
    {SectionDivider}
    <EditSection
      disabled={isEditingAdditionalInfo}
      isImmutable={isEditMode}
      label={t($ => $.promotions.create.step4.loyaltyValue)}
      onEditButtonClicked={() => navigateToStep(1)}
      value={
        promotion.uom === RewardsUom.Dollars
          ? `${t(
            $ => $.promotions.create.step4.earnPerDollar,
            {
              amount: new Intl.NumberFormat(i18next.language, {
                style: 'currency',
                currency: getCurrencyForLocale(i18next.language),
                currencyDisplay: 'symbol',
                minimumFractionDigits: promotion.rewardType === RewardsType.Dollars ? 4 : 3,
                maximumFractionDigits: promotion.rewardType === RewardsType.Dollars ? 4 : 3,
              }).format(promotion.value ?? 0),
            },
          )}`
          : `${t(
            $ => $.promotions.create.step4.earnPerUnit,
            {
              amount: new Intl.NumberFormat(i18next.language, {
                style: 'currency',
                currency: getCurrencyForLocale(i18next.language),
                currencyDisplay: 'symbol',
                minimumFractionDigits: 4,
                maximumFractionDigits: 4,
              }).format(promotion.value ?? 0),
              uom: promotion.uom,
            },
          )}`
      }
    />
    {SectionDivider}
    {!!promotion.pointCap && (
      <>
        <EditSection
          disabled={isEditingAdditionalInfo}
          label={t($ => $.promotions.create.step4.loyaltyCap)}
          onEditButtonClicked={() => navigateToStep(1)}
          value={getLoyaltyPointsText(promotion, t)}
        />
        {SectionDivider}
      </>
    )}
    <EditSection
      disabled={isEditingAdditionalInfo}
      isImmutable={isEditMode}
      label={t($ => $.promotions.create.step4.redeemingPoints)}
      onEditButtonClicked={() => navigateToStep(0)}
      value={promotion.redeemOnAllProducts
        ? promotion.sponsorType === SponsorType.Retailer
          ? t($ => $.promotions.create.step4.allProducts)
          : t($ => $.promotions.create.step4.allManufacturerProducts)
        : t($ => $.promotions.create.step4.selectProducts)
      }
    />
    {SectionDivider}
    <EditSection
      disabled={isEditingAdditionalInfo}
      label={t($ => $.promotions.create.step4.earnProducts)}
      onEditButtonClicked={() => navigateToStep(2)}
      onValueClicked={() => setShowProductsForEarningSideSheet(true)}
      value={`${totalProductsForEarning} ${totalProductsForEarning === 1 ? "product" : "products"}`}
    />
    {!!totalProductsForRedeeming && (
      <>
        {SectionDivider}
        <EditSection
          disabled={isEditingAdditionalInfo}
          label={t($ => $.promotions.create.step4.redeemProducts)}
          onEditButtonClicked={() => navigateToStep(3)}
          onValueClicked={() => setShowProductsForRedeemingSideSheet(true)}
          value={`${totalProductsForRedeeming} ${
            totalProductsForRedeeming === 1 ? "product" : "products"
          }`}
        />
      </>
    )}
    {SectionDivider}
    <EditSection
      disabled={isEditingAdditionalInfo}
      label={t($ => $.promotions.create.step4.dashboardDisplay)}
      onEditButtonClicked={() => navigateToStep(0)}
    >
      <EditSectionSubLine
        label="Include the promotion within the 'Product Promotions’ carousel on the dashboard"
        value={promotion.showInDashboardCarousel}
      />
      <VSpacer size="4" />
      <EditSectionSubLine
        label="Include the promotion within the 'point value' table on the 'Program Benefits'
        tier details page"
        value={promotion.showInDashboardTable}
      />
      <VSpacer size="4" />
    </EditSection>
    {!!activeRewardsProgram?.grantBothProgramAndPromotionPoints && (
      <>
        {SectionDivider}
        <EditSection
          disabled={isEditingAdditionalInfo}
          isImmutable={isEditMode}
          label={t($ => $.promotions.create.step4.engineOverride)}
          onEditButtonClicked={() => navigateToStep(0)}
        >
          <EditSectionSubLine
            label="Only allow manufacturer promotion points to be granted. Retailer rewards program
        points are not granted for the products included in this promotion."
            value={promotion.overridePointsEngine}
          />
        </EditSection>
      </>
    )}
    {SectionDivider}
    <EditSection
      disabled={isEditingAdditionalInfo}
      isImmutable={isEditMode}
      label={t($ => $.promotions.create.step4.manualAdministration)}
      onEditButtonClicked={() => navigateToStep(0)}
    >
      <EditSectionSubLine
        label={t($ => $.promotions.create.step1.doNotAutoGrantPoints)}
        value={promotion.grantsPointsManually}
      />
    </EditSection>
    <VSpacer size="6" />
    <AdditionalInfoSection
      isEditing={isEditingAdditionalInfo}
      onCancel={() => setIsEditingAdditionalInfo(false)}
      onEditButtonClicked={() => setIsEditingAdditionalInfo(true)}
      onSave={(description) => {
        onPromotionChanged({
          ...promotion,
          description: description.trim() || undefined,
        });
        setIsEditingAdditionalInfo(false);
      }}
      promotion={promotion}
    />
    <VSpacer size="10" />
    {!!selectedRetailer && !!retailerLocations?.length && showEditLocationsModal && (
      <EditLocationsModal
        onClose={() => setShowEditLocationsModal(false)}
        onPromotionChanged={onPromotionChanged}
        promotion={promotion}
        retailerLocations={retailerLocations}
        selectedRetailer={selectedRetailer}
      />
    )}
    {(!!totalProductsForEarning && showProductsForEarningSideSheet) && (
      <AddedProductsSideSheet
        onChange={(productIds) => onPromotionChanged({
          ...promotion,
          productIdsForEarningPoints: productIds.length === 0 ? undefined : productIds,
        })}
        onClose={() => setShowProductsForEarningSideSheet(false)}
        productIds={promotion.productIdsForEarningPoints ?? []}
        productType={'earn'}
        readonly
      />
    )}
    {(!!totalProductsForRedeeming && showProductsForRedeemingSideSheet) && (
      <AddedProductsSideSheet
        onChange={(productIds) => onPromotionChanged({
          ...promotion,
          productIdsForRedeemingPoints: productIds.length === 0 ? undefined : productIds,
        })}
        onClose={() => setShowProductsForRedeemingSideSheet(false)}
        productIds={promotion.productIdsForRedeemingPoints ?? []}
        productType={'redeem'}
        readonly
      />
    )}
  </>;
};
